import { CSSProperties, useState } from 'react';
import { IDataProvider, IDataRequest } from '@vivli/features/data-requests/infrastructure/interface';
import { FormProvider, useFormContext, useFormState } from 'react-hook-form';
import { ButtonView } from '@vivli/shared/views';
import { useDataRequestProviders, useUnlistedStudyForm } from '@vivli/features/data-requests/infrastructure/hook';
import { AddUnlistedStudyFormComponent } from './add-unlisted-study-form.component';

import { first } from 'rxjs/operators';
import { Color, Size } from '@vivli/shared/theme';
import { LoggerService } from '@vivli/shared/infrastructure/service';
import { DTICommonConst } from '@vivli/shared/infrastructure/constants';
import { useDataRequestsApiContext, useModalApiContext } from '@vivli/core/infrastructure/context';

const containerStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    backgroundColor: Color.VIVLI_DARK_BLUE,
    color: Color.WHITE,
    minWidth: 300,
    borderRadius: 15
};

const headerStyle: CSSProperties = {
    padding: Size.PADDING * 2,
    borderBottom: '1px solid white',
    fontSize: Size.FontSize.Larger,
    alignContent: 'center'
};

const bodyContainerStyle: CSSProperties = {
    padding: Size.PADDING * 2,
    fontSize: Size.FontSize.Medium
};

const scrollContainerStyle: CSSProperties = {
    display: 'flex',
    padding: Size.PADDING,
    overflowY: 'scroll',
    height: 530,
    width: 850,
    flexDirection: 'column',
    color: 'rgb(96, 104, 111)',
    backgroundColor: 'rgb(246, 247, 251)'
};

const infoTextStyle: CSSProperties = {
    fontSize: Size.FontSize.Large,
    lineHeight: '1.4',
    fontFamily: 'Verdana, sans-serif'
};

const infoTextStyleSuccess: CSSProperties = {
    ...infoTextStyle,
    color: Color.VIVLI_DARKER_GREEN
};

const buttonContainerStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-evenly'
};

const resultContainerStyle: CSSProperties = {
    paddingTop: '40px',
    paddingBottom: '40px'
};

export interface AddUnlistedStudyComponentProps {
    dataRequestId: string;
    onClose: () => void;
    onStudyAdded: (dataRequest?: IDataRequest) => void;
    isInProcess?: boolean;
}

const DEFAULT_ADD_STUDY_ERROR = 'There was a problem adding an external data item to your Data Request. Please contact Vivli for help.';

// ExceptionHandler.cs writes JsonConvert.SerializeObject(exception.Message), so the 400 body is a JSON-encoded string.
const parseAddStudyError = (err: unknown): string => {
    if (typeof err !== 'string' || err.length === 0) return DEFAULT_ADD_STUDY_ERROR;
    try {
        const parsed = JSON.parse(err);
        return typeof parsed === 'string' && parsed.length > 0 ? parsed : DEFAULT_ADD_STUDY_ERROR;
    } catch {
        return err;
    }
};

export const AddUnlistedStudyComponent = ({ dataRequestId, onStudyAdded, onClose, isInProcess }: AddUnlistedStudyComponentProps) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [activeProvider, setActiveProvider] = useState<IDataProvider>();
    const [studyError, setStudyError] = useState<string | null>(null);
    const [submittedData, setSubmittedData] = useState<any>(null);

    const { reset, handleSubmit } = useFormContext();
    const { isValid } = useFormState();

    const dataRequestsService = useDataRequestsApiContext();
    const modalService = useModalApiContext();
    const providers = useDataRequestProviders(dataRequestId, isInProcess);

    const resetForm = () => {
        reset();
        setActiveProvider(null);
        setStudyError(null);
    };

    const handleOnCancel = () => {
        onClose && onClose();
    };

    const doAddExternalData = (requestData: any) => {
        setIsSubmitting(true);
        const addObservable = isInProcess
            ? dataRequestsService.addUnlistedStudyToInProcessRequest(dataRequestId, requestData)
            : dataRequestsService.addUnlistedStudy(dataRequestId, requestData);

        addObservable.pipe(first()).subscribe(
            (dataRequest) => {
                const provider = providers.find((p) => p.id === requestData.dataProviderOrgId);
                setActiveProvider(provider);

                LoggerService.debug('dataRequestAddEtag', dataRequest._etag);
                onStudyAdded && onStudyAdded(dataRequest);
                setSubmittedData(requestData);
            },
            (err) => {
                setStudyError(parseAddStudyError(err));
            },
            () => {
                setIsSubmitting(false);
            }
        );
    };

    const handleOnSubmit = () => {
        handleSubmit(({ dataProviderOrgId, nctOrSponsorProtocolId, studyTitle }: any) => {
            const isNct = nctOrSponsorProtocolId.toLowerCase().startsWith('nct');
            const requestData = {
                studyTitle,
                dataProviderOrgId,
                nctId: isNct && nctOrSponsorProtocolId,
                sponsorProtocolId: !isNct && nctOrSponsorProtocolId
            };

            setIsSubmitting(true);
            dataRequestsService
                .checkForListedStudy(nctOrSponsorProtocolId)
                .pipe(first())
                .subscribe(
                    (checkResult) => {
                        if (checkResult.found) {
                            setIsSubmitting(false);
                            const matchInfo = checkResult.nctId
                                ? `NCT ID ${checkResult.nctId} (Sponsor Protocol ID ${checkResult.sponsorProtocolId})`
                                : `Sponsor Protocol ID ${checkResult.sponsorProtocolId}`;
                            modalService.confirm(
                                <div style={{ textAlign: 'left' }}>
                                    The study with {matchInfo} is listed on the platform.
                                    <br />
                                    <br />
                                    To add this listed study, click "Cancel" and add this study via the “Add Listed Study” button. To
                                    continue adding the study as an unlisted study, click “OK".
                                </div>,
                                {
                                    title: 'Listed Study with Requested ID Found',
                                    confirmText: 'OK',
                                    cancelText: 'Cancel',
                                    onConfirm: () => doAddExternalData(requestData)
                                }
                            );
                        } else {
                            doAddExternalData(requestData);
                        }
                    },
                    () => {
                        // If the check fails, proceed with the add anyway (fail open)
                        doAddExternalData(requestData);
                    }
                );
        })();
    };

    return (
        <div>
            <div style={containerStyle}>
                <div style={headerStyle}>Add External Data to this Data Request</div>
                <div style={bodyContainerStyle}>
                    <div style={scrollContainerStyle} className="scrolly">
                        <div>
                            <div style={infoTextStyle}>
                                If you are bringing in non-Vivli data, select “External Data” and add a name and description. You will be
                                notified when it’s time to upload, and the Vivli team will support you. Any data that is brought into the
                                Vivli secure research environment must be added to your data request here, it must be anonymized
                                (de-identified or pseudonymized data cannot be accepted), and you must have permission from the data owner
                                to bring it in.
                            </div>
                            {!studyError && !activeProvider && <AddUnlistedStudyFormComponent providers={providers} />}
                            {!studyError && activeProvider && (
                                <div style={resultContainerStyle}>
                                    <div style={infoTextStyleSuccess}>
                                        External data item {
                                            submittedData?.sponsorProtocolId && submittedData.sponsorProtocolId !== 'false'
                                                ? submittedData.sponsorProtocolId
                                                : submittedData?.nctId ?? 'Unknown Id'
                                        } is successfully added to the Data Request.
                                    </div>
                                </div>
                            )}
                            {studyError && (
                                <div style={resultContainerStyle}>
                                    <div style={infoTextStyle}>{studyError}</div>
                                </div>
                            )}
                            <div style={buttonContainerStyle}>
                                {!studyError && (
                                    <ButtonView
                                        onClick={!activeProvider ? handleOnSubmit : resetForm}
                                        disabled={!isValid}
                                        isLoading={isSubmitting}
                                        dataId={DTICommonConst.SubmitButton}
                                    >
                                        {!activeProvider ? 'Add External Data' : 'Add Another External Data Item'}
                                    </ButtonView>
                                )}
                                <ButtonView
                                    style={{ backgroundColor: Color.VIVLI_RED }}
                                    onClick={handleOnCancel}
                                    className="vivliInfoModal_close"
                                    dataId={DTICommonConst.CancelButton}
                                >
                                    {!activeProvider && !studyError ? 'Cancel' : 'Back'}
                                </ButtonView>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
